'use client';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import styles from './LiveEventPanelInfo.module.scss';
import { LoadingButton } from '@eventbrite/marmalade';
import {
  getPanelDisplayPrice,
  shouldShowDiscountSignal,
  shouldShowCheckoutButton,
  shouldShowSoldOutText,
} from '../utils';
import { getDisplayTimezone, getSingleDateDisplayString } from '@/utils/dateTime';
import { FormattedMessage } from 'react-intl';
import { LoadingSkeleton } from '@eventbrite/legacy-infra.loading-skeleton';
import { useLiveEventData } from '../hooks/useLiveEventData';
import { CheckoutButton } from '../CheckoutButton/CheckoutButton';
import { MULTIPLE_DATES, DONATION_TEXT } from '@/components/_shared/constants';
import { useAttendeeTimezone } from '@/hooks/useAttendeeTimezone';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton';
import { CONVERSION_BAR_MESSAGES } from '../constants';
import { HighLevelStatus } from '@/utils/highLevelStatus';
import { useSalesStatus } from '@/components/_shared/contexts/SalesStatusContext';
import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';

const DetailsLoadingSkeleton: React.FC = () => {
  return (
    <div className={styles.detailsLoadingSkeleton}>
      <LoadingSkeleton height={15} width={150} />
      <LoadingSkeleton height={15} width={150} />
    </div>
  );
};

const ContentArea: React.FC = () => {
  const { loading } = useLiveEventData();

  if (loading) {
    return <DetailsLoadingSkeleton />;
  }
  return <SalesInformation />;
};

const SalesInformation: React.FC = () => {
  const { formatMessage, locale } = useIntl();
  const { ticketInfo, startDate, isSeries, isOnline, hideStartDate } = useLiveEventData();
  const { endDate, hideEndDate } = useBasicInfo();

  const attendeeTimezone = useAttendeeTimezone(isOnline ?? false);
  const { displayStatus } = useSalesStatus();

  if (!ticketInfo || !startDate) {
    return null;
  }

  let displayText;
  if (ticketInfo.hasOnlyDonationTicketsAvailable) {
    displayText = formatMessage(DONATION_TEXT);
  } else if (shouldShowSoldOutText(displayStatus, ticketInfo)) {
    displayText = CONVERSION_BAR_MESSAGES[HighLevelStatus.SOLD_OUT]
      ? formatMessage(CONVERSION_BAR_MESSAGES[HighLevelStatus.SOLD_OUT])
      : '';
  } else {
    displayText = getPanelDisplayPrice(ticketInfo, locale, formatMessage);
  }

  const showEndTimeOnly = hideStartDate && !hideEndDate && endDate?.utc;
  const timezone = getDisplayTimezone({
    isOnlineEvent: isOnline ?? false,
    eventTimezone: startDate.timezone,
    attendeeTimezone,
  });
  const dateString = isSeries
    ? formatMessage(MULTIPLE_DATES)
    : getSingleDateDisplayString(
        startDate,
        !hideStartDate,
        locale,
        timezone,
        formatMessage,
        false,
        !showEndTimeOnly ? ' • ' : undefined,
        true,
        isOnline ?? false,
      );

  return (
    <>
      <div data-testid="conversion-bar-headline" className={styles.headline}>
        <span>{displayText}</span>
      </div>
      <div data-testid="conversion-bar-date" className={styles.date}>
        {dateString}
      </div>
    </>
  );
};

export const LiveEventPanelInfo: React.FC<{ showDiscountSignal?: (show: boolean) => void }> = ({
  showDiscountSignal = () => void {},
}) => {
  const { status } = useBasicInfo();
  const { ticketInfo, loading } = useLiveEventData();
  useEffect(() => {
    if (shouldShowDiscountSignal(ticketInfo) && !ticketInfo?.hasOnlyDonationTicketsAvailable) {
      showDiscountSignal(true);
    }
  }, [ticketInfo, showDiscountSignal, ticketInfo?.hasOnlyDonationTicketsAvailable]);

  const { displayStatus } = useSalesStatus();
  const showCheckoutButton = shouldShowCheckoutButton(status, displayStatus, ticketInfo);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <ContentArea />
      </div>
      <div data-testid="cb-cta-area" className={styles.ctaButtonWrapper}>
        {loading ? (
          <LoadingButton
            isLoading={loading}
            id="conversion-bar-checkout-button"
            data-testid="conversion-bar-checkout-button"
            role="button"
            variant="primary"
            className={styles.button}
          >
            <FormattedMessage id="Get tickets" defaultMessage="Get tickets" description="Checkout button CTA" />
          </LoadingButton>
        ) : showCheckoutButton ? (
          <CheckoutButton isRemindMe={false} />
        ) : (
          <ExploreSimilarEventsButton />
        )}
      </div>
    </div>
  );
};
